import React from "react"
import { Col, ResponsiveRow, Row } from "@theguarantors/ui-kit-v2/dist/layout/grid"
import { Button, FormStep, Divider, TextField, CurrencyField, AutocompleteSelectField } from "@theguarantors/ui-kit-v2"
import { FormProps, UnenrolledFormValues } from "./UnenrolledPage.types"
import { Form } from "formik"
import { states, USAState } from "../../constants"
import { shouldDisableSubmitButton } from "@theguarantors/ui-kit-v2/dist/utils/one-column-form"
import { useBeforeUnload } from "@theguarantors/ui-kit-v2/dist/hooks"
import { FeatureFlag, useFeatureFlag } from "../../hooks/use-feature-flag"
import { useHistoryActions } from "../../history"
import { useLocation, useNavigate } from "react-router-dom"

function filterSuggestions(query: string): USAState[] {
  return states.filter((state: USAState) => state.name.toLocaleLowerCase().startsWith(query.toLocaleLowerCase()))
}

function getSuggestionValue(state: USAState): string {
  return state.name
}

function hasSomeValue(values: UnenrolledFormValues): boolean {
  return Object.values(values).some(Boolean)
}

export const UnenrolledForm: React.FC<FormProps> = ({ dirty, isSubmitting, values, errors, isValid }) => {
  const { goToLandingPage } = useHistoryActions()
  const location = useLocation()
  const navigate = useNavigate()
  // Should warn user (via browser popup) about possible data loss in case of page refresh if:
  // 1) user filled in some form data on unenrolled page
  // 2) user didn't change anything and tried to refresh with only a pre-filled data from renters page (building, unit)
  useBeforeUnload(dirty || hasSomeValue(values))

  React.useEffect(() => {
    if (location.state) {
      navigate(location, {
        replace: true,
        state: undefined,
      })
    }
    // eslint-disable-next-line
  }, [])

  const [rebrandV2MonolithReact] = useFeatureFlag(FeatureFlag.REBRAND_V2_MONOLITH_REACT)
  const rebrandV2MonolithReactFeatureFlag: Boolean = rebrandV2MonolithReact === "on"

  return (
    <Form noValidate data-testid="unenrolled-form">
      <FormStep.Content>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="email" placeholder="Email Address" required data-testid="email" />
          </Col>
        </Row>
        <Divider orientation={["left", "center"]}>New Home Info</Divider>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField
              name="addressStreet"
              placeholder="Your Next Building Address"
              required
              data-testid="addressStreet"
            />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="apartmentNumber" placeholder="Unit #" data-testid="apartmentNumber" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="addressCity" placeholder="City" required data-testid="addressCity" />
          </Col>
        </Row>
        <ResponsiveRow hgutter={[undefined, "2.8rem"]} vgutter={["s", "xl"]}>
          <Col>
            <AutocompleteSelectField<USAState>
              name="addressState"
              required
              placeholder="State"
              suggestions={states}
              getSuggestionValue={getSuggestionValue}
              filterSuggestions={filterSuggestions}
              data-testid="addressState"
            />
          </Col>
          <Col>
            <TextField name="zipCode" placeholder="Zip Code" required data-testid="zipCode" />
          </Col>
        </ResponsiveRow>

        <Divider orientation={["left", "center"]}>Agent Info</Divider>

        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="landlordCompany" placeholder="Landlord's Company" data-testid="landlordCompany" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="landlordName" placeholder="Landlord's Full Name" data-testid="landlordName" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="landlordEmail" placeholder="Landlord's Email" data-testid="landlordEmail" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="brokerName" placeholder="Broker's Full Name" data-testid="brokerName" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <TextField name="brokerEmail" placeholder="Broker's Email" data-testid="brokerEmail" />
          </Col>
        </Row>

        <Divider orientation={["left", "center"]}>Your Financial Info</Divider>

        <Row vgutter={["s", "xl"]}>
          <Col>
            <CurrencyField name="annualIncome" placeholder="Annual Income" required data-testid="annualIncome" />
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <CurrencyField name="liquidAsset" placeholder="Liquid Assets" required data-testid="liquidAsset" />
          </Col>
        </Row>
      </FormStep.Content>
      <FormStep.Actions>
        <Button
          variant={rebrandV2MonolithReactFeatureFlag ? "v2_primary" : "primary"}
          type="submit"
          disabled={shouldDisableSubmitButton({ dirty, isSubmitting })}
          children="Submit"
          data-testid="submit-button"
        />
        <Button variant="link" onClick={goToLandingPage} children="Back" data-testid="back-button" />
      </FormStep.Actions>
    </Form>
  )
}
